import React, { useState, useEffect, useRef } from 'react';
import OverviewPage  from './pages/OverviewPage';
import InventoryPage from './pages/InventoryPage';
import AuditsPage    from './pages/AuditsPage';
import ReportsPage   from './pages/ReportsPage';
import AlertsPage    from './pages/AlertsPage';
import { getLabs, updateLab, deleteLab } from './services/api';
import './App.css';

const NAV = [
  { id: 'overview',   label: 'Overview',   icon: '📊' },
  { id: 'inventory',  label: 'Inventory',  icon: '🧪' },
  { id: 'audits',     label: 'Audits',     icon: '📋' },
  { id: 'reports',    label: 'Reports',    icon: '📈' },
  { id: 'alerts',     label: 'Alerts',     icon: '🔔' },
];

export default function App() {
  const [page, setPage]     = useState('overview');
  const [labs, setLabs]     = useState([]);
  const [labId, setLabId]   = useState(null);
  const [labName, setLabName] = useState('All Labs');

  // edit/delete modal state
  const [editModal, setEditModal]   = useState(null);   // lab object being edited
  const [editNameVal, setEditNameVal] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const editInputRef = useRef(null);

  const reloadLabs = () =>
    getLabs().then(r => {
      setLabs(r.data);
    }).catch(() => {});

  useEffect(() => {
    getLabs().then(r => {
      setLabs(r.data);
      // Auto-select lab from URL query param (e.g. from email alert link)
      const urlLabId = new URLSearchParams(window.location.search).get('lab_id');
      if (urlLabId) {
        const match = r.data.find(l => String(l.id) === String(urlLabId));
        if (match) { setLabId(match.id); setLabName(match.name); return; }
      }
      if (r.data.length > 0) {
        setLabId(r.data[0].id);
        setLabName(r.data[0].name);
      }
    }).catch(() => {});
  }, []);

  const handleLabChange = (e) => { // eslint-disable-line no-unused-vars
    const val = e.target.value;
    if (val === '') {
      setLabId(null);
      setLabName('All Labs');
    } else {
      const lab = labs.find(l => String(l.id) === String(val));
      setLabId(Number(val));
      setLabName(lab?.name || '');
    }
  };

  const openEdit = (lab, e) => {
    e.stopPropagation();
    setEditModal(lab);
    setEditNameVal(lab.name);
    setTimeout(() => editInputRef.current?.focus(), 80);
  };

  const saveRename = async () => {
    if (!editNameVal.trim()) return;
    setEditSaving(true);
    try {
      await updateLab(editModal.id, { name: editNameVal.trim(), color: editModal.color });
      if (labId === editModal.id) setLabName(editNameVal.trim());
      await reloadLabs();
      setEditModal(null);
    } catch {
      alert('Could not rename lab.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (lab, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete lab "${lab.name}"?\n\nThis will permanently delete ALL its items and audit history.`)) return;
    try {
      await deleteLab(lab.id);
      if (labId === lab.id) { setLabId(null); setLabName('All Labs'); }
      await reloadLabs();
    } catch {
      alert('Could not delete lab.');
    }
  };

  const pageProps = { labId, labName };

  return (
    <div className="dash-wrap">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-brand">
          <svg width="36" height="36" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <rect x="10" y="72" width="80" height="7" rx="3.2" fill="#D7E4FA"/>
            <rect x="15" y="48" width="19" height="24" rx="2" fill="#E9F1FF"/>
            <rect x="15" y="48" width="19" height="6.5" rx="1.6" fill="#C7DAF7"/>
            <path d="M44 32 H53 V37 C53 37 57.5 40 57.5 47 L57.5 66 C57.5 69.9 54.3 73 50.4 73 H46.6 C42.7 73 39.5 69.9 39.5 66 L39.5 47 C39.5 40 44 37 44 37 Z" fill="#E9F1FF"/>
            <rect x="42.5" y="27" width="12" height="5" rx="2" fill="#D7E4FA"/>
            <rect x="39.8" y="54" width="17.4" height="12" rx="0" fill="#0C8464"/>
            <path d="M63 43 C63 41 65 40 71 40 C77 40 79 41 79 43 L79 65 C79 69.4 75.4 73 71 73 C66.6 73 63 69.4 63 65 Z" fill="#E9F1FF"/>
            <rect x="67" y="36" width="8" height="7" rx="2" fill="#D7E4FA"/>
            <rect x="63.6" y="55" width="14.8" height="8.5" fill="#0C8464" opacity="0.92"/>
            <g transform="translate(60,2)">
              <circle cx="14" cy="14" r="15" fill="#FFCB05"/>
              <path d="M7 14.5 L12 19.5 L21.5 8.5" stroke="#0A2A5E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            </g>
          </svg>
          <div>
            <div className="brand-name">HLA Lab IMS</div>
            <div className="brand-sub">Pycube · U of Michigan</div>
          </div>
        </div>

        {/* Lab selector */}
        <div style={{ padding: '0 16px 12px' }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>
            Lab
          </div>
          {/* "All Labs" row */}
          <div
            onClick={() => { setLabId(null); setLabName('All Labs'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
              borderRadius: 8, cursor: 'pointer', marginBottom: 2,
              background: labId === null ? '#334155' : 'transparent',
              color: '#f1f5f9', fontSize: 13, fontWeight: labId === null ? 700 : 500,
            }}
          >
            <span style={{ flex: 1 }}>All Labs</span>
          </div>
          {labs.map(l => (
            <div
              key={l.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '6px 6px 6px 10px',
                borderRadius: 8, cursor: 'pointer', marginBottom: 2,
                background: labId === l.id ? '#334155' : 'transparent',
              }}
              onClick={() => { setLabId(l.id); setLabName(l.name); }}
            >
              <span
                style={{
                  width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                  background: l.color || '#0A2A5E',
                }}
              />
              <span style={{ flex: 1, color: '#f1f5f9', fontSize: 13, fontWeight: labId === l.id ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {l.name}
              </span>
              <button
                title="Rename"
                onClick={(e) => openEdit(l, e)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 13, opacity: 0.7, lineHeight: 1 }}
              >✏️</button>
              <button
                title="Delete"
                onClick={(e) => handleDelete(l, e)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', fontSize: 13, opacity: 0.7, lineHeight: 1 }}
              >🗑️</button>
            </div>
          ))}
        </div>

        <nav className="sidebar-nav">
          {NAV.map(n => (
            <button
              key={n.id}
              className={`nav-item ${page === n.id ? 'active' : ''}`}
              onClick={() => setPage(n.id)}
            >
              <span className="nav-icon">{n.icon}</span>
              <span className="nav-label">{n.label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          <div style={{ fontSize: 11, color: '#94a3b8' }}>University of Michigan</div>
          <div style={{ fontSize: 11, color: '#64748b' }}>Pycube IMS v1.0</div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        {page === 'overview'  && <OverviewPage  {...pageProps} />}
        {page === 'inventory' && <InventoryPage {...pageProps} />}
        {page === 'audits'    && <AuditsPage    {...pageProps} />}
        {page === 'reports'   && <ReportsPage   {...pageProps} />}
        {page === 'alerts'    && <AlertsPage    {...pageProps} />}
      </main>

      {/* Rename modal */}
      {editModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }} onClick={() => setEditModal(null)}>
          <div
            style={{
              background: '#fff', borderRadius: 16, padding: 28, width: 360,
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 800, color: '#0f172a' }}>Rename Lab</h3>
            <input
              ref={editInputRef}
              value={editNameVal}
              onChange={e => setEditNameVal(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setEditModal(null); }}
              placeholder="Lab name"
              style={{
                width: '100%', padding: '11px 14px', borderRadius: 10, fontSize: 15,
                border: '1.5px solid #e2e8f0', outline: 'none', boxSizing: 'border-box',
                background: '#f8fafc',
              }}
            />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button
                onClick={saveRename}
                disabled={editSaving || !editNameVal.trim()}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 10, border: 'none',
                  background: '#0A2A5E', color: '#fff', fontSize: 14, fontWeight: 700,
                  cursor: editSaving ? 'default' : 'pointer', opacity: editSaving ? 0.6 : 1,
                }}
              >{editSaving ? 'Saving…' : 'Save'}</button>
              <button
                onClick={() => setEditModal(null)}
                style={{
                  flex: 1, padding: '11px 0', borderRadius: 10, border: 'none',
                  background: '#f1f5f9', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
